import React, { useEffect } from "react";
import { X } from "lucide-react";

/**
 * Universal teaching-clip player modal.
 * Supports YouTube, Instagram embeds, and direct video files.
 *
 * Props:
 *   clip = { url, title, platform: "youtube"|"instagram"|"vimeo"|"direct" }
 *   onClose: () => void
 */
function youtubeId(url) {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/
  );
  return m ? m[1] : null;
}

function vimeoId(url) {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(\d+)/);
  return m ? m[1] : null;
}

export default function VideoClipModal({ clip, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!clip) return null;

  const platform = clip.platform || "direct";
  const ytId = platform === "youtube" ? youtubeId(clip.url) : null;
  const vimId = platform === "vimeo" ? vimeoId(clip.url) : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="video-clip-modal"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
        aria-label="Close"
        data-testid="clip-modal-close"
      >
        <X className="w-5 h-5 text-white" />
      </button>

      <div
        className="w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        {clip.title && (
          <div className="text-white text-base sm:text-lg font-semibold mb-3 text-center">
            {clip.title}
          </div>
        )}

        <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden border border-white/10">
          {ytId ? (
            <iframe
              key={ytId}
              src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
              title={clip.title || "Teaching clip"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          ) : vimId ? (
            <iframe
              key={vimId}
              src={`https://player.vimeo.com/video/${vimId}?autoplay=1`}
              title={clip.title || "Teaching clip"}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          ) : platform === "instagram" ? (
            // Instagram does not allow iframe embeds without API auth — open in new tab.
            <div className="flex flex-col items-center justify-center h-full text-center p-6">
              <div className="text-white/70 text-sm mb-4">Instagram clips open in a new tab</div>
              <a
                href={clip.url}
                target="_blank"
                rel="noreferrer"
                className="bg-[#FF0066] hover:bg-[#e6005c] text-white text-sm font-semibold rounded-full px-5 py-2.5"
              >
                Open on Instagram ↗
              </a>
            </div>
          ) : (
            <video src={clip.url} controls autoPlay className="w-full h-full" />
          )}
        </div>
      </div>
    </div>
  );
}
